import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, Building2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", code: "" };

export default function Branches() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/branches").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      if (editId) await api.put(`/branches/${editId}`, form);
      else await api.post("/branches", form);
      toast.success(editId ? "Cabang diperbarui" : "Cabang ditambahkan");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/branches/${id}`); toast.success("Cabang dihapus"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="branches-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-gold">Jaringan</p>
          <h1 className="mt-1 font-serif text-4xl font-bold text-brand-espresso">Cabang</h1>
        </div>
        <Button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-branch-btn"
          className="gap-2 bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">
          <Plus className="h-4 w-4" /> Tambah Cabang
        </Button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-brand-line bg-white shadow-[0_8px_30px_rgba(45,35,30,0.05)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-line bg-brand-surface/60 text-left text-xs uppercase tracking-wide text-brand-muted">
              <th className="px-6 py-4 font-semibold">Nama Cabang</th>
              <th className="px-6 py-4 font-semibold">Kode</th>
              <th className="px-6 py-4 text-right font-semibold">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={3} className="py-12 text-center text-brand-muted">Belum ada cabang.</td></tr>
            ) : items.map((b) => (
              <tr key={b.id} className="border-b border-brand-line/60 hover:bg-brand-surface/40" data-testid={`branch-row-${b.id}`}>
                <td className="px-6 py-4 font-medium text-brand-espresso"><span className="inline-flex items-center gap-2"><Building2 className="h-4 w-4 text-brand-gold" />{b.name}</span></td>
                <td className="px-6 py-4"><span className="rounded-full bg-brand-surface px-2.5 py-0.5 text-xs font-semibold text-brand-espresso">{b.code}</span></td>
                <td className="px-6 py-4 text-right">
                  <Button variant="ghost" size="sm" onClick={() => { setForm({ name: b.name, code: b.code }); setEditId(b.id); setOpen(true); }} data-testid={`edit-branch-${b.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" data-testid={`delete-branch-${b.id}`} className="text-destructive hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Hapus cabang?</AlertDialogTitle>
                        <AlertDialogDescription>Cabang "{b.name}" akan dihapus. Hanya bisa jika belum ada pesanan.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(b.id)} className="bg-destructive hover:bg-destructive/90">Hapus</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Ubah Cabang" : "Tambah Cabang"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama Cabang</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="branch-name-input" className="mt-1.5" placeholder="mis. Bandung" />
            </div>
            <div>
              <Label>Kode Cabang</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="branch-code-input" className="mt-1.5" placeholder="mis. BDG" maxLength={6} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={busy || !form.name || !form.code} data-testid="save-branch-btn" className="bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
